import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Plus, Edit, RefreshCw, Wifi, AlertTriangle, Trash2 } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { Dialog } from '@/components/ui/Dialog';
import { useHotspots, useCreateHotspot, useUpdateHotspot, useDeleteHotspot } from '@/hooks/useTelecom';
import { STATUT_HOTSPOT_LABELS, type StatutHotspot, type Hotspot } from '@/types';
import { extractError } from '@/api/client';
import { useAuthStore } from '@/stores/authStore';
import { demander } from '@/components/ui/Fenetres';

const STATUT_BADGE: Record<StatutHotspot, string> = {
  actif:        'ds-bd ds-bd-s',
  maintenance:  'ds-bd ds-bd-w',
  hors_service: 'ds-bd ds-bd-d',
};

export const HotspotsPage = () => {
  const filiale = useAuthStore((s) => s.filiale);
  const [statut, setStatut]         = useState<StatutHotspot | ''>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing]       = useState<Hotspot | undefined>();
  const [error, setError]           = useState<string | null>(null);

  const { data, isLoading, refetch } = useHotspots({ statut: statut || undefined, actif: true, limit: 200 });
  const hotspots = data?.data ?? [];
  const remove = useDeleteHotspot();

  const openCreate = () => { setEditing(undefined); setDialogOpen(true); };
  const openEdit   = (h: Hotspot) => { setEditing(h); setDialogOpen(true); };

  const count = (s: StatutHotspot) => hotspots.filter((h) => h.statut === s).length;

  const onRemove = async (id: string, code: string) => {
    if (!(await demander.confirmer({ titre: `Supprimer le hotspot "${code}" ?`, confirmer: 'Supprimer', danger: true }))) return;
    setError(null);
    try { await remove.mutateAsync(id); }
    catch (err) { setError(extractError(err).message); }
  };

  return (
    <>
      <Topbar
        title="Carte hotspots WiFi"
        subtitle={filiale ? `${hotspots.length} hotspot(s) · ${filiale.fullName}` : 'Hotspots'}
        actions={
          <>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()}>
              <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
            </button>
            <button className="ds-btn ds-btn-p" onClick={openCreate}>
              <Plus size={14} /> Nouveau hotspot
            </button>
          </>
        }
      />
      <div className="ds-content">
        {error && (
          <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 16 }}>
            <span className="ds-alert-ico"><AlertTriangle size={18} /></span>
            <div className="ds-alert-txt">{error}</div>
          </div>
        )}

        <div className="ds-kpi-row" style={{ marginBottom: 16 }}>
          <div className="ds-kr kr-c1">
            <div className="ds-kr-ico">📡</div>
            <div className="ds-kr-val">{hotspots.length}</div>
            <div className="ds-kr-lbl">Hotspots</div>
          </div>
          <div className="ds-kr kr-c3">
            <div className="ds-kr-ico">✅</div>
            <div className="ds-kr-val">{count('actif')}</div>
            <div className="ds-kr-lbl">Actifs</div>
          </div>
          <div className="ds-kr kr-c4">
            <div className="ds-kr-ico">🔧</div>
            <div className="ds-kr-val">{count('maintenance')}</div>
            <div className="ds-kr-lbl">Maintenance</div>
          </div>
          <div className="ds-kr kr-c5">
            <div className="ds-kr-ico">⛔</div>
            <div className="ds-kr-val">{count('hors_service')}</div>
            <div className="ds-kr-lbl">Hors service</div>
          </div>
        </div>

        <div className="ds-filters">
          <select className="ds-fi" value={statut} onChange={(e) => setStatut(e.target.value as StatutHotspot | '')} style={{ width: 'auto' }}>
            <option value="">Tous statuts</option>
            {(Object.keys(STATUT_HOTSPOT_LABELS) as StatutHotspot[]).map((s) => (
              <option key={s} value={s}>{STATUT_HOTSPOT_LABELS[s]}</option>
            ))}
          </select>
        </div>

        <div className="ds-table-panel">
          <div className="ds-table-header">
            <div>
              <div className="ds-table-title">Hotspots</div>
              <div className="ds-table-sub">{hotspots.length} hotspot{hotspots.length > 1 ? 's' : ''}</div>
            </div>
          </div>
          <table className="ds-tbl">
            <thead>
              <tr>
                <th style={{ width: 100 }}>Code</th>
                <th>Nom</th>
                <th>Quartier / Ville</th>
                <th>FAI / Bande</th>
                <th className="ds-am" style={{ width: 90 }}>Abonnés</th>
                <th style={{ width: 100 }}>Statut</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'var(--tx-3)' }}>Chargement…</td></tr>}
              {!isLoading && hotspots.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: 48 }}>
                    <Wifi size={28} style={{ margin: '0 auto 8px', color: 'var(--tx-3)' }} />
                    <p style={{ fontSize: 13, color: 'var(--tx-3)' }}>Aucun hotspot</p>
                    <button className="ds-btn ds-btn-p ds-btn-sm" style={{ marginTop: 12 }} onClick={openCreate}>
                      <Plus size={12} /> Créer le premier hotspot
                    </button>
                  </td>
                </tr>
              )}
              {!isLoading && hotspots.map((h) => (
                <tr key={h.id}>
                  <td><span className="ds-ref-code">{h.code}</span></td>
                  <td>
                    <div className="ds-entry-label">{h.nom}</div>
                    {h.adresse && <div className="ds-entry-sub">{h.adresse}</div>}
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {h.quartier ?? '—'}
                    {h.ville && <div style={{ fontSize: 10, color: 'var(--tx-3)' }}>{h.ville}</div>}
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {h.fournisseurInternet ?? '—'}
                    {h.bandwidthMbps && <div style={{ fontSize: 10, color: 'var(--tx-3)' }}>{h.bandwidthMbps} Mbps</div>}
                  </td>
                  <td className="ds-am" style={{ fontWeight: 600 }}>{h._count?.abonnes ?? 0}</td>
                  <td><span className={STATUT_BADGE[h.statut]}>{STATUT_HOTSPOT_LABELS[h.statut]}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button onClick={() => openEdit(h)} className="ds-row-btn" title="Modifier"><Edit size={13} /></button>
                      <button onClick={() => onRemove(h.id, h.code)} className="ds-row-btn" title="Supprimer" disabled={remove.isPending}><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <HotspotFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} hotspot={editing} />
    </>
  );
};

const HotspotFormDialog = ({ open, onClose, hotspot }: { open: boolean; onClose: () => void; hotspot?: Hotspot }) => {
  const isEdit = !!hotspot;
  const [code, setCode]             = useState('');
  const [nom, setNom]               = useState('');
  const [adresse, setAdresse]       = useState('');
  const [quartier, setQuartier]     = useState('');
  const [ville, setVille]           = useState('');
  const [fai, setFai]               = useState('');
  const [bandwidth, setBandwidth]   = useState('');
  const [capacite, setCapacite]     = useState('');
  const [statut, setStatut]         = useState<StatutHotspot>('actif');
  const [notes, setNotes]           = useState('');
  const [error, setError]           = useState<string | null>(null);

  const create = useCreateHotspot();
  const update = useUpdateHotspot();

  useEffect(() => {
    if (!open) return;
    if (hotspot) {
      setCode(hotspot.code); setNom(hotspot.nom);
      setAdresse(hotspot.adresse ?? ''); setQuartier(hotspot.quartier ?? '');
      setVille(hotspot.ville ?? ''); setFai(hotspot.fournisseurInternet ?? '');
      setBandwidth(hotspot.bandwidthMbps?.toString() ?? ''); setCapacite(hotspot.capaciteUtilisateurs?.toString() ?? '');
      setStatut(hotspot.statut); setNotes(hotspot.notes ?? '');
    } else {
      setCode(''); setNom(''); setAdresse(''); setQuartier(''); setVille(''); setFai('');
      setBandwidth(''); setCapacite(''); setStatut('actif'); setNotes('');
    }
    setError(null);
  }, [open, hotspot]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const payload = {
        code: code.trim(), nom: nom.trim(),
        adresse: adresse.trim() || undefined, quartier: quartier.trim() || undefined, ville: ville.trim() || undefined,
        fournisseurInternet: fai.trim() || undefined,
        bandwidthMbps: bandwidth ? parseInt(bandwidth, 10) : undefined,
        capaciteUtilisateurs: capacite ? parseInt(capacite, 10) : undefined,
        statut, notes: notes.trim() || undefined,
      };
      if (isEdit && hotspot) await update.mutateAsync({ id: hotspot.id, payload });
      else await create.mutateAsync(payload);
      onClose();
    } catch (err) { setError(extractError(err).message); }
  };

  const canSubmit = code.trim() && nom.trim();

  return (
    <Dialog open={open} onClose={onClose} title={isEdit ? `Modifier — ${hotspot?.code}` : 'Nouveau hotspot WiFi'} subtitle="Sinergie WeFly" size="lg">
      <form onSubmit={onSubmit} className="space-y-3">
        {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"><AlertTriangle className="inline size-3 mr-1" />{error}</div>}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Code *"><input value={code} onChange={(e) => setCode(e.target.value)} className="ds-fi" required style={{ width: '100%' }} placeholder="HSP-001" /></Field>
          <Field label="Statut"><select value={statut} onChange={(e) => setStatut(e.target.value as StatutHotspot)} className="ds-fi" style={{ width: '100%' }}>{(Object.keys(STATUT_HOTSPOT_LABELS) as StatutHotspot[]).map((s) => <option key={s} value={s}>{STATUT_HOTSPOT_LABELS[s]}</option>)}</select></Field>
        </div>
        <Field label="Nom *"><input value={nom} onChange={(e) => setNom(e.target.value)} className="ds-fi" required style={{ width: '100%' }} placeholder="Cocody Riviera Golf" /></Field>
        <Field label="Adresse"><input value={adresse} onChange={(e) => setAdresse(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="Rue, immeuble, étage…" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Quartier"><input value={quartier} onChange={(e) => setQuartier(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="Cocody" /></Field>
          <Field label="Ville"><input value={ville} onChange={(e) => setVille(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="Abidjan" /></Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="FAI"><input value={fai} onChange={(e) => setFai(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="Orange CI" /></Field>
          <Field label="Bande (Mbps)"><input type="number" min="1" value={bandwidth} onChange={(e) => setBandwidth(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="100" /></Field>
          <Field label="Capacité simultanée"><input type="number" min="1" value={capacite} onChange={(e) => setCapacite(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="50" /></Field>
        </div>
        <Field label="Notes"><textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="ds-fi" style={{ width: '100%' }} /></Field>
        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={!canSubmit || create.isPending || update.isPending}>{isEdit ? 'Enregistrer' : 'Créer le hotspot'}</button>
        </div>
      </form>
    </Dialog>
  );
};

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div><label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>{children}</div>
);
